import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { AiGeneratedChip, OutlinedIcons, RichTextDisplay } from '@marageti/z4-lib';
import { TravelerProfileDetailsUpdate } from '@marageti/z4-sdk/lib/people';
import useTravelerProfile from '../../hooks/use-traveler-profile';
import EditRichTextFieldModal from './edit-rich-text-field-modal';
import SuggestionSlot from '../traveler-profile-suggestions/suggestion-slot';
import SuggestionSkeleton from '../traveler-profile-suggestions/suggestion-skeleton';
import DismissedSuggestionRow from '../traveler-profile-suggestions/dismissed-suggestion-row';
import SuggestionUndoButton from '../traveler-profile-suggestions/suggestion-undo-button';
import { useTextSuggestionPreview } from '../../hooks/use-traveler-profile-suggestion-preview';
import { SuggestionTextField } from '../../store/contexts/traveler-profile-suggestions/types';

const {
  OutlinedTagIcon,
  OutlinedGiftIcon,
  OutlinedUsersIcon,
} = OutlinedIcons;

// ---------------------------------------------------------------------------
// Rich text section (display + edit trigger)
// ---------------------------------------------------------------------------

type RichTextSectionProps = {
  icon: React.ReactNode;
  label: string;
  /** Noun used to build the action label, e.g. "Programs" → "Edit Programs" / "+Add Programs" */
  actionNoun: string;
  content?: string;
  onEditClick?: () => void;
  emptyMessage?: string;
  suggestionField: SuggestionTextField;
};

const RichTextSection = ({
  icon,
  label,
  actionNoun,
  content,
  onEditClick,
  emptyMessage = 'No information added yet.',
  suggestionField,
}: RichTextSectionProps) => {
  const preview = useTextSuggestionPreview(suggestionField, { hasContent: !!content });
  // While an AI import review is in flight, lock fields the AI didn't return
  // content for so the user can't half-edit profile data mid-review.
  const lockedForReview = preview.state === 'idle' && preview.lockedForReview;

  const renderHeader = (showChip = false) => (
    <Stack direction="row" alignItems="center" spacing={2}>
      {icon}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body">{label}</Typography>
        {showChip && <AiGeneratedChip />}
      </Stack>
    </Stack>
  );

  if (preview.state === 'pending') {
    return (
      <Stack spacing={1}>
        {renderHeader(true)}
        <SuggestionSlot
          showChip={false}
          disabled={preview.disabled}
          onApply={preview.apply}
          onDismiss={preview.dismiss}
        >
          <Box sx={{ color: 'text.secondary' }}>
            <RichTextDisplay>{preview.value}</RichTextDisplay>
          </Box>
        </SuggestionSlot>
      </Stack>
    );
  }

  // Once the user clicks "Apply to Profile" the suggestion is staged in
  // memory but not yet persisted. Preview the staged value in place of the
  // empty/saved field so the page looks like the data has landed; the actual
  // PUT happens on "Save changes". Replace the Edit button with Undo so the
  // user can back out a single field without aborting the whole review.
  if (preview.state === 'accepted') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <RichTextDisplay>{preview.value}</RichTextDisplay>
        <SuggestionUndoButton onClick={preview.undo} />
      </Stack>
    );
  }

  // Dismissed: leave a quiet placeholder so the page structure stays stable
  // and the user can recover the suggestion mid-review.
  if (preview.state === 'dismissed') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <DismissedSuggestionRow onUndo={preview.undo} />
      </Stack>
    );
  }

  if (preview.state === 'loading') {
    return (
      <Stack spacing={1}>
        {renderHeader()}
        <SuggestionSkeleton lines={2} />
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      {renderHeader()}

      {content ? (
        <RichTextDisplay>{content}</RichTextDisplay>
      ) : (
        <Typography variant="body" color="common.black50">
          {emptyMessage}
        </Typography>
      )}

      {onEditClick && !lockedForReview && (
        <Button
          variant="text"
          size="small"
          onClick={onEditClick}
          sx={{ p: 0, minWidth: 0, alignSelf: 'flex-start' }}
        >
          <Typography variant="captionBold" color="primary.main">
            {content ? `Edit ${actionNoun}` : `+Add ${actionNoun}`}
          </Typography>
        </Button>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

type EditModalKey = 'loyalty' | 'gifts' | 'household';

const FIELD_MAP: Record<EditModalKey, keyof TravelerProfileDetailsUpdate> = {
  loyalty: 'loyaltyPrograms',
  gifts: 'giftsReceived',
  household: 'householdDetails',
};

type TravelerProfileRichTextSectionsProps = {
  travelerId: string | undefined;
  canEdit: boolean;
};

const TravelerProfileRichTextSections = ({
  travelerId,
  canEdit,
}: TravelerProfileRichTextSectionsProps) => {
  const { travelerProfile, updateTravelerProfile } = useTravelerProfile(travelerId);
  const [editModal, setEditModal] = useState<EditModalKey | null>(null);

  const handleSave = async (field: EditModalKey, value: string) => {
    await updateTravelerProfile(
      { [FIELD_MAP[field]]: value },
      'Error saving changes.',
      'Saved successfully.',
    );
  };

  const modalConfig: Record<EditModalKey, {
    title: string;
    label: string;
    value: string;
    onSave: (v: string) => void;
  }> = {
    loyalty: {
      title: 'Loyalty Programs',
      label: 'Loyalty Programs',
      value: travelerProfile?.loyaltyPrograms ?? '',
      onSave: (v) => handleSave('loyalty', v),
    },
    gifts: {
      title: 'Gifts Received',
      label: 'Gifts Received',
      value: travelerProfile?.giftsReceived ?? '',
      onSave: (v) => handleSave('gifts', v),
    },
    household: {
      title: 'Household Details',
      label: 'Household Details',
      value: travelerProfile?.householdDetails ?? '',
      onSave: (v) => handleSave('household', v),
    },
  };

  const activeModal = editModal ? modalConfig[editModal] : null;

  return (
    <>
      <RichTextSection
        icon={<OutlinedTagIcon fontSize="small" />}
        label="Loyalty Programs"
        actionNoun="Programs"
        content={travelerProfile?.loyaltyPrograms}
        onEditClick={canEdit ? () => setEditModal('loyalty') : undefined}
        emptyMessage="Not recorded"
        suggestionField="loyaltyPrograms"
      />

      <Divider />

      <RichTextSection
        icon={<OutlinedGiftIcon fontSize="small" />}
        label="Gifts Received"
        actionNoun="Gifts"
        content={travelerProfile?.giftsReceived}
        onEditClick={canEdit ? () => setEditModal('gifts') : undefined}
        emptyMessage="Not recorded"
        suggestionField="giftsReceived"
      />

      <Divider />

      <RichTextSection
        icon={<OutlinedUsersIcon fontSize="small" />}
        label="Household Details"
        actionNoun="Details"
        content={travelerProfile?.householdDetails}
        onEditClick={canEdit ? () => setEditModal('household') : undefined}
        emptyMessage="Not recorded"
        suggestionField="householdDetails"
      />

      {activeModal && (
        <EditRichTextFieldModal
          open={editModal !== null}
          onClose={() => setEditModal(null)}
          title={activeModal.title}
          label={activeModal.label}
          value={activeModal.value}
          onSave={activeModal.onSave}
        />
      )}
    </>
  );
};

export default TravelerProfileRichTextSections;
